import { Team, Domain, User, Improvement, Currency, AlertNew, Account, Platform } from '../index'
import { TinyInt } from '../utils/global'

export interface DomainInformation {
    domainId: Domain.ID
    accountId: Account.ID
    domainName: string
    currency: Currency.CurrencyCode
    customerId: string
    hasBudgetAutoPause: TinyInt | null
}

export interface AccountInformation {
    accountId: Account.ID
    domainId?: Domain.ID // legacy gads account id
    customerId: string
    platform: number
    domainName: string
    initials: string
    color: string
    currency: Currency.CurrencyCode
    hasBudgetAutoPause: TinyInt | null
}

export interface GetImpactStatisticsRequest {
    teamId: Team.ID
    fromDate: Date
    toDate: Date
}

export interface GetImpactStatisticsResponse {
    teamImpactStatistics: TeamImpactStatistics
    domainImpactStatistics: DomainImpactStatistics[]
    userImpactStatistics: UserImpactStatistics[]
    CSVs: CSVs
}

interface BaseImpactStatistics {
    costSavings: CostSavings
    timeSavings: TimeSavings
    criticalAlertCounts: CriticalAlertCounts
}

export interface TeamImpactStatistics extends BaseImpactStatistics {
    teamId: Team.ID
    keywordMetrics: KeywordMetrics
    teamStatistics: Actions
}

export interface DomainImpactStatistics extends BaseImpactStatistics {
    domainId: Domain.ID
    domainName: string
    keywordMetrics: { keywords: Keyword[]; total: KeywordMetrics }
}

export interface CSVs {
    costSavingsCSV: string
    edumacationCostSavingsCSV: string
    timeSavingsCSV: string
    criticalAlertCountsCSV: string
    keywordMetricsCSV: string
    userStatisticsCSV: string
}

// COST SAVINGS

export interface BaseCostSavings {
    monthlyCostSavingsGBP: number
    monthlyCostSavingsUSD: number
    yearlyCostSavingsGBP: number
    yearlyCostSavingsUSD: number
}

export interface CostSavingsCounts extends BaseCostSavings {
    count: number
}

export interface ImprovementCostSavings {
    improvementId: number
    recAction: RecActionWithCostSavings
    type: Improvement.Type
    ts: Date
    title: string
    staticTitle: string
    location: Improvement.AccountLocation[]
    accountId: Account.ID
    monthlyCostSavingsGBP: number
    monthlyCostSavingsUSD: number
    yearlyCostSavingsGBP: number
    yearlyCostSavingsUSD: number
}

export interface PauseSpendCostSavings {
    accountId: Account.ID
    monthlyCostSavingsGBP: number
    monthlyCostSavingsUSD: number
    yearlyCostSavingsGBP: number
    yearlyCostSavingsUSD: number
    hasBudgetAutoPause: boolean
}

export interface NgramCostSavings extends BaseCostSavings {
    accountId: Account.ID
    keyword: string
    date: string
}

export interface CostSavings {
    total: CostSavingsCounts
    improvements: {
        instances: ImprovementCostSavings[]
        recActions: {
            [recAction in Improvement.RecAction]?: CostSavingsCounts
        }
        total: CostSavingsCounts
    }
    pauseSpend: BaseCostSavings & { hasBudgetAutoPause: boolean }
    ngram: {
        instances: NgramCostSavings[]
        total: BaseCostSavings
    }
}

export interface AccountCostTableOverview {
    id: Account.ID
    account: AccountInformation
    impCount: number
    avgSavedPerImp: number
    totalCostSaved: number
    accountBreakdown: {
        costSavingInstances: CostSavingOverviewInstance[]
        accountInfo: AccountInformation
        accountId: Account.ID
    }
}

export enum OverviewItemInstanceType {
    'pauseSpend' = 'pauseSpend',
    'improvement' = 'improvement',
    'alert' = 'alert',
    'ngram' = 'ngram',
}

export interface CostSavingOverviewInstance {
    type: OverviewItemInstanceType
    title: string
    account: AccountInformation
    savings: number
    location: Improvement.AccountLocation[]
    improvementId?: number
    date: string
    hasBudgetAutoPause?: boolean
}

export interface CostSavingOverviewItem {
    improvementType: string
    count: number
    avgSavedPerImp: number
    totalCostSaved: number
    timeSavedPerImp: string
    totalTimeSaved: string
    seeMore: CostSavingOverviewInstance[]
}

export interface GetCostSavedResponse {
    overviewItems: CostSavingOverviewItem[]
    costTableData: AccountCostTableOverview[]
    teamCostSavings: CostSavings
    accountCostSavings: { [accountId: Account.ID]: CostSavings }
    costSavingsCSV: string
    edumacationCostSavingsCSV: string
    subscriptionCost: number
}

export interface GetPotentialCostSavedResponse {
    teamCostSavings: CostSavings
    accountCostSavings: { [accountId: Account.ID]: CostSavings }
    costSavingsCSV: string
    edumacationCostSavingsCSV: string
}

// TIME SAVINGS

export interface TimeSavings {
    improvementsCount: number
    improvementsMinutesSaved: number
    mostRecentImprovement: Date | null
    createdRsaCount: number
    createdRsaMinutesSaved: number
    mostRecentCreatedRsa: Date | null
    updatedRsaCount: number
    updatedRsaMinutesSaved: number
    mostRecentUpdatedRsa: Date | null
    biddingExperimentsCount: number
    biddingExperimentsMinutesSaved: number
    mostRecentBiddingExperiment: Date | null
    reportsCount: number
    reportsMinutesSaved: number
    mostRecentReport: Date | null
    scorecardsCount: number
    scorecardsMinutesSaved: number
    mostRecentScorecard: Date | null
    ngramCount: number
    ngramMinutesSaved: number
    mostRecentNgram: Date | null
    totalMinutesSaved: number
}

export interface TimeSavingsOverviewItem {
    actionType: string
    count: number
    timeSavedPerAction: string
    timeSavedPerActionMinutes: number
    timeSavedPerUser: string
    mostRecent: Date | string
    totalTimeSaved: number
}

export interface AccountTimeTableOverview {
    id: Account.ID
    account: AccountInformation
    totalTimeSaved: number
    improvements: number
    improvementsMinutesSaved: number
    reports: number
    reportsMinutesSaved: number
    toolActions: number
    toolActionsMinutesSaved: number
}

export interface GetTimeSavedResponse {
    timeTableData: AccountTimeTableOverview[]
    overviewItems: TimeSavingsOverviewItem[]
    teamTimeSavings: TimeSavings
    accountTimeSavings: { [accountId: Account.ID]: TimeSavings }
    timeSavingsCSV: string
}

// CRITICAL ALERT COUNTS

export interface CriticalAlert {
    alertId: AlertNew.ID
    type: AlertNew.AlertType
    accountId: Account.ID
    ts: Date
    hash_alert_id: string
    is_email_sent: TinyInt
    isConversion: boolean | null // not null when alert type is conversion, true if conversion alert and false is cpa alert
    title: string
}

export interface CriticalImprovementAlert {
    improvementId: number
    accountId: Account.ID
    recAction: Improvement.RecAction
    ts: Date
}

export interface CriticalAlertCounts {
    budgetAlertCount: number
    budgetAlertEmails: number
    deltaAlertCount: number
    deltaAlertEmails: number
    impressionsFlatlineAlertCount: number
    impressionsFlatlineAlertEmails: number
    conversionActionsFlatlineAlertCount: number
    conversionActionsFlatlineAlertEmails: number
    brokenLinkImprovementCount: number
    totalCriticalAlertCount: number
}

export interface AccountCriticalAlertCounts extends CriticalAlertCounts {
    instances: { alerts: CriticalAlert[]; improvements: CriticalImprovementAlert[] }
    currencyCode: Currency.CurrencyCode
}

export interface CriticalAlertOverviewInstance {
    type: OverviewItemInstanceType
    account: AccountInformation
    title: string
    ts: Date
    date: string
    hashAlertId?: string
    improvementId?: number
}

export interface CriticalAlertOverviewItem {
    alertType: string
    count: number
    averagePerWeek: number
    mostRecent: Date | string
    emailsSent: number
    seeMore: CriticalAlertOverviewInstance[]
}

export interface AccountAlertTableOverview {
    id: Account.ID
    account: AccountInformation
    budget: number
    kpiAnom: number
    zeroImpr: number
    zeroConv: number
    brokenLink: number
    accountBreakdown: {
        instances: CriticalAlertOverviewInstance[]
        accountInfo: AccountInformation
    }
}

export interface GetCriticalAlertCountsResponse {
    overviewItems: CriticalAlertOverviewItem[]
    alertsTableData: AccountAlertTableOverview[]
    teamCriticalAlertCount: CriticalAlertCounts
    accountCriticalAlertCounts: { [accountId: Account.ID]: AccountCriticalAlertCounts }
    criticalAlertCountsCSV: string
}

// KEYWORD METRICS

export interface KeywordMetrics {
    allConversions: number
    conversionsValueGBP: number
    conversionsValueUSD: number
}

export interface Keyword extends KeywordMetrics {
    keyword: string
    matchType: string
    campaignName: string
    adGroupName: string
}

export interface GetKeywordMetricsResponse {
    teamKeywordMetrics: KeywordMetrics
    domainKeywordMetrics: { [domainId: Domain.ID]: { keywords: Keyword[]; total: KeywordMetrics } }
    keywordMetricsCSV: string
}

// USER STATISTICS

export interface Actions {
    pushedImprovementCount: number
    createdReportCount: number
    createdRsaCount: number
    updatedRsaCount: number
    createdExperimentCount: number
    sessionCount: number
    userCreatedScorecardCount: number
    autoCreatedScorecardCount: number
    nGramFinderNegativesAdded: number
}

export interface UserImpactStatistics extends Actions {
    userId: User.ID
    username: string
    name: string
    role: User.Role
}

export interface GetUserStatisticsResponse {
    userStatistics: UserImpactStatistics[]
    teamStatistics: Actions
    userStatisticsCSV: string
}

export interface UserStatistics {
    userStatistics: UserImpactStatistics[]
}

export interface ActionCount {
    userId: string
    label: string
    date: string
    value: number
}

export interface Activity {
    label: string
    value: number
}
export interface HeatmapChartItem {
    total: number
    date: string | Date
    activities?: Activity[]
}

export interface TeamImpactStatisticsSettings {
    includeTimeSavingsInCostSavings: boolean
    hourlyRate: number
    timeSavedPerImprovement: number
    timeSavedPerReport: number
    timeSavedPerToolAction: number
    timeSavedPerCreatedRsa: number
    timeSavedPerUpdatedRsa: number
    timeSavedPerBiddingExperiment: number
    timeSavedPerScorecard: number
    timeSavedPerNgram: number
}

export interface UserInfo {
    userId: User.ID
    username: string
    name: string
    role: User.Role
    image: string
}

export interface CalendarHeatMap {
    calendarHeatMap: HeatmapChartItem[]
}

export type UserActivities = (UserInfo & CalendarHeatMap)[]

export interface GetCalendarHeatMapResponse {
    userActivities: UserActivities
    teamActivities: CalendarHeatMap
}

export interface GetTeamAccountsResponse {
    accountId: Account.ID
    domainId: Domain.ID
    name: string
    color: string
    platform: Platform.Platform
    initials: string
}

export const recActionWithoutCostSavings = [
    'add_call_extension',
    'add_callout_extension',
    'add_campaign_level_callout_extension',
    'add_campaign_level_sitelink_extension',
    'add_campaign_level_snippet_extension',
    'add_missing_campaigns_to_shared_set',
    'add_negative_list_shopping',
    'add_negative_word',
    'add_site_exclusions',
    'add_sitelink_extension',
    'add_skag',
    'add_skag_v2',
    'add_structured_snippets_extension',
    'adjust_ad_group_multi_targets',
    'adjust_ad_schedule_bids',
    'adjust_ad_schedule_bids_v2',
    'adjust_single_age_bid',
    'adjust_single_gender_bid',
    'adjust_single_parental_status_bid',
    'adjust_single_income_bid',
    'adjust_multiple_age_bids',
    'adjust_multiple_gender_bids',
    'adjust_multiple_parental_status_bids',
    'adjust_multiple_income_bids',
    'adjust_age_bid',
    'adjust_attribution_model',
    'adjust_device_bid',
    'adjust_gender_bid',
    'adjust_geo_bids',
    'adjust_keyword_bid',
    'adjust_parental_bid',
    'adjust_product_group_bid',
    'check_ad_spelling',
    'create_brand_campaign',
    'create_remarketing_campaign',
    'disable_interest_locations',
    'duplicate_keyword',
    'exclude_placement',
    'exclude_mobile_apps',
    'fix_below_first_page_bid',
    'fix_broken_sitelink',
    'fix_negatives_blocking_keywords',
    'increase_keyword_bid_v2',
    'lift_campaign_budget_cap',
    'pause_ad',
    'pause_duplicate_keywords',
    'pause_keyword',
    'reduce_word_bids',
    'remove_broad_keywords',
    'remove_plussed_negatives',
    'resync_exclusion_lists',
    'resync_product_groups',
    'search_partners_settings',
    'subdivide_product_group',
    'write_ad',
    'write_ad_v2',
    'adjust_single_device_bid',
    'adjust_multiple_device_bids',
    'increase_keyword_bid_ms',
    'adjust_keyword_bid_ms',
    'disable_image_templates',
    'disable_image_touchups',
    'disable_text_optimizations',
    'disable_video_auto_crop',
    'ga_disable_ad_group_optimized_targeting_enabled',
    'ga_disable_broad_match_as_default',
    'ga_opt_out_text_asset_automation',
    'ga_account_recommend_limited_brand_safety_suitability',
    'ga_account_toggle_recommendation_subscriptions',
    'disable_audience_expansion_li',
    'adjust_creative_selection_li',
    'adjust_ad_targeting_facet_location_li',
    'ms_account_get_auto_apply_opt_in_status',
    'ms_campaign_people_in_or_searching_for_or_viewing_pages',
    'ms_campaign_auto_generated_text_opt_out',
    'ms_campaign_final_url_expansion_opt_out',
    'ms_campaign_auto_generated_image_opt_out',
    'ms_campaign_auto_generated_assets_enabled',
    'recheck_disapproved_ad',
    'recheck_disapproved_keyword',
    'recheck_empty_campaign',
    'recheck_empty_ad_group',
    'recheck_broken_link',
    'recheck_failed_learning',
    'recheck_spelling_error',
    'recheck_reactivated_keyword',
    'recheck_reactivated_ad_group',
] as const

export type RecActionWithoutCostSavings = (typeof recActionWithoutCostSavings)[number]

export type RecActionWithCostSavings = Exclude<Improvement.RecAction, RecActionWithoutCostSavings>
